import { useEffect, useRef, useState } from 'react';
import * as ApiModels from '@typings/api-models';
import * as format from '@format';
import * as DocumentApiModels from '@/tsClient/servers/noodleDocuments/document-api-models';
import FileType from '@components/Icons/FileType';
import PencilSimple from '@components/Icons/PencilSimple';
import Eye from '@components/Icons/Eye';
import XCircle from '@components/Icons/XCircle';
import { usePrevious, useIsTextTruncated } from '@hooks';
import DocumentUploadButton from '@components/Document/DocumentUploadButton';
import DocumentUploader from '@components/Document/DocumentUploader';
import { useOpenPreviewDocumentPane } from '@panes/hooks';
import File from '@/components/DesignLibrary/File';
import Image from 'next/image';
import PACERLogo from '@lib/Creators/Dashboard/Integrations/assets/pacer.png';
import Modal from '@/components/Modal';
import BannerStatus from '@/components/DesignLibrary/BannerStatus';
import { DocumentAccessProvider } from '@/providers/DocumentAccess';
import DocumentPreview from '@/lib/DocumentPreview/DocumentPreview';
import s from './DocumentsListModal.module.scss';
import DocumentSummary from './DocumentSummary';
import DocumentVersion from './DocumentVersion';

type UploadedDocument = Parameters<NonNullable<Parameters<typeof DocumentUploader>[0]['onFinishUpload']>>[0];
type Props = {
  creatorId?: string;
  compiledDocument:
    & Pick<ApiModels.UserWorkflowDocument, 'id' | 'referenceId' | 'referenceType' | 'templateReferenceType'| 'title' | 'integrationSlug' | 'integrationFileName'>
    & {
      document: Pick<DocumentApiModels.Document, 'id' | 'mimeType' | 'fileName' | 'createdAt' | 'rejectedAt' | 'rejectionMessage'> | null;
    }
    & Parameters<typeof DocumentSummary>[0]['compiledDocument']
    & Parameters<typeof DocumentVersion>[0]['compiledDocument']
  onChangeTitle?: (documentId: string, title: string) => void;
  onUploadComplete?: (document: UploadedDocument) => Promise<void> | void;
  isSaving?: boolean;
  fileNameOnly?: boolean;
  hideViewButton?: boolean;
  viewInModal?: boolean;
};

const DocumentRow: React.FC<Props> = ({
  creatorId, compiledDocument, onChangeTitle, onUploadComplete, isSaving, fileNameOnly, hideViewButton, viewInModal,
}) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [originalTitle, setOriginalTitle] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const previousIsSaving = usePrevious(isSaving ?? false);
  const isEmpty = !compiledDocument.document;
  const canManuallyUpload = (compiledDocument.templateReferenceType === 'manually-added'
    || compiledDocument.templateReferenceType === 'document-request-file' || compiledDocument.templateReferenceType === 'ai-bot') && creatorId && onUploadComplete;
  const openDocumentRequestDocumentPane = useOpenPreviewDocumentPane();
  const rejectionMessage = compiledDocument?.document?.rejectionMessage || 'Rejected At';
  const rejectedAt = compiledDocument?.document?.rejectedAt || null;
  const label = `${rejectedAt ? `${rejectionMessage} (${format.datetimeLocalTZ.withoutTimezone({ datetime: rejectedAt })})` : ''}`;

  const titleRef = useRef(null);
  const isTitleTruncated = useIsTextTruncated(titleRef);

  useEffect(() => {
    if (!previousIsSaving && isSaving) {
      setIsEditingTitle(false);
    }
  }, [previousIsSaving, isSaving]);

  const handleClickEdit = (): void => {
    setIsEditingTitle(true);
    setOriginalTitle(compiledDocument.title);
  };

  const handleClickCancel = (): void => {
    setIsEditingTitle(false);
    if (onChangeTitle) {
      onChangeTitle(compiledDocument.id, originalTitle || '');
    }
  };

  const handleChangeTitle = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (onChangeTitle) {
      onChangeTitle(compiledDocument.id, e.target.value);
    }
  };

  return (
    <>
      <div className={(isEmpty || (isEmpty && compiledDocument.templateReferenceType === 'manually-added')) ? s['row-container-empty'] : s['row-container']}>
        {compiledDocument.integrationSlug === 'pacer' && <Image src={PACERLogo} alt="Pacer" width={24} height={24} className={s.logo} />}
        {compiledDocument.document && !isEmpty && fileNameOnly
          ? <div className={s.parentFileWrapper}>
            {rejectedAt && (
              <div className={s.rejectionMessage}>
                <BannerStatus
                  status="error"
                  label={label}
                />
              </div>
            )}
            <File document={{
              createdAt: compiledDocument.document.createdAt,
              fileName: compiledDocument.integrationFileName || compiledDocument.document.fileName,
              id: compiledDocument.document.id,
              mimeType: compiledDocument.document.mimeType,
              uploadedAt: '',
              uploadedBy: { name: null },
            }} allowRename={!compiledDocument.integrationFileName} />
          </div>
          : <></>
        }
        {(isEmpty || !fileNameOnly) && <>
          {!fileNameOnly && <div className={s.icon}>
            <FileType mimeType={compiledDocument.document?.mimeType} size={24} isEmpty={isEmpty} />
          </div>}
          <div className={s['row-main-part']}>
            <>
              {isEditingTitle
                ? (
                  <div className={s['name-edit-wrapper']}>
                    <button onClick={handleClickCancel}>
                      <XCircle weight="fill" size={16} color="var(--color-error)" />
                    </button>
                    <input type="text" id={`${compiledDocument.id}-title`} onChange={handleChangeTitle} defaultValue={compiledDocument.title || ''} />
                  </div>
                )
                : (
                  <div className={s['name-view-wrapper']}>
                    <p
                      ref={titleRef}
                      title={(() => {
                        if (!isTitleTruncated) return undefined;
                        if (fileNameOnly) return compiledDocument.document?.fileName;
                        return compiledDocument.title || compiledDocument.document?.fileName;
                      })()}
                      className={s.name}>
                      {fileNameOnly
                        ? <>{compiledDocument.document?.fileName || 'No file'}</>
                        : <>
                          {compiledDocument.title || compiledDocument.document?.fileName}
                        </>}
                    </p>
                    {!fileNameOnly && <button onClick={handleClickEdit}>
                      <PencilSimple weight="fill" size={16} color="var(--color-gray-100)" />
                    </button>}
                  </div>
                )}
              <DocumentSummary compiledDocument={compiledDocument} fileNameOnly={fileNameOnly} />
              <DocumentVersion compiledDocument={compiledDocument} />
            </>
          </div>
        </>}
        {canManuallyUpload && (
          <DocumentUploadButton creatorId={creatorId} id={`in-row-${1}`} isInRow={true} onUploadComplete={d => onUploadComplete(d)} />
        )}
        {compiledDocument.document && !hideViewButton && (
          <button className={s['circle-button']} onClick={() => {
            if (compiledDocument.document) {
              if (viewInModal) {
                setModalOpen(true);
              } else {
                openDocumentRequestDocumentPane({ previewDocumentId: compiledDocument.document.id });
              }
            }
          }}>
            <Eye size={16} weight="fill" />
          </button>
        )}
      </div>
      {modalOpen && compiledDocument.document && (
        <Modal onClose={() => setModalOpen(false)} title="Document Preview" drag>
          <DocumentAccessProvider>
            <DocumentPreview documentId={compiledDocument.document.id} isModal />
          </DocumentAccessProvider>
        </Modal>
      )}
    </>
  );
};

export default DocumentRow;
